import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import api, { resetAuthErrorState } from '../services/api';
import { useAuth } from './AuthContext';
import { useLoader } from './LoaderContext';
import dayjs from 'dayjs';


const SiteVisitContext = createContext();

export const useSiteVisit = () => useContext(SiteVisitContext);

export const SiteVisitProvider = ({ children }) => {
  // Get authentication state from AuthContext
  const { isAuthenticated } = useAuth();
  
  // State for site visits
  const [siteVisits, setSiteVisits] = useState([]);
  const [allSiteVisits, setAllSiteVisits] = useState([]);
  const [filters, setFilters] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasApiError, setHasApiError] = useState(false);
  const [statusCounts, setStatusCounts] = useState({});
  
 
  // Applied filters
  const [appliedFilters, setAppliedFilters] = useState({});
    
  // Add pagination state
  const [paginationInfo, setPaginationInfo] = useState({
    total_count: 0,
    current_page: 1,
    page_size: 50,
    total_pages: 1,
    has_next: false,
    has_previous: false
  });

  // Track if we've done initial data loading
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);

  // Add a ref to track the last API call to prevent duplicate calls
  const lastCallRef = useRef(null);
  
  // Add a ref to track the last status counts API call
  const lastStatusCountsCallRef = useRef(null);
  
  // Add a ref for debouncing API calls
  const debounceTimeoutRef = useRef(null);
  
  // Add a ref to track the current request ID
  const currentRequestIdRef = useRef(null);

  // Fetch site visits with POST request (similar to LeadContext)
  const fetchSiteVisits = useCallback(async (filters = {}, dateRangeState = null, searchKeyword = null, forceRefresh = false) => {
    // Prevent duplicate calls with better comparison
    const callKey = JSON.stringify({ 
      filters: filters || {}, 
      dateRangeState: dateRangeState ? {
        start_date: dateRangeState.currentStart?.format('YYYY-MM-DD'),
        end_date: dateRangeState.currentEnd?.format('YYYY-MM-DD'),
        filter_id: dateRangeState.filter?.id
      } : null, 
      searchKeyword: searchKeyword || null,
      all_results: filters.all_results || false
    });
    
    // Check if this is the same call as the last one (unless forceRefresh is true)
    if (lastCallRef.current === callKey && !forceRefresh) {
      return;
    }
    
    // Prevent multiple simultaneous calls (unless forceRefresh is true, which allows sorting/pagination)
    if (isLoading && !forceRefresh) {
      return;
    }
    
    // Update the last call reference BEFORE making the call
    lastCallRef.current = callKey;
    
    // Generate a unique request ID for this call
    const requestId = Date.now() + Math.random();
    currentRequestIdRef.current = requestId;

    setIsLoading(true);
    setError(null);

    try {
      // Build the request payload
      const requestPayload = {
        // Always include pagination and sorting
        page: filters.page || 1,
        page_size: filters.page_size || 50,
        sort_field: filters.sort_field || '-updated_at',
        sort_order: filters.sort_order || 'desc',
        is_deleted: filters.is_deleted || false,
        all_results: filters.all_results || false
      };

      // Add search if provided
      if (searchKeyword && typeof searchKeyword === 'string' && searchKeyword.trim()) {
        requestPayload.search = searchKeyword.trim();
      }

      // Add other filters (excluding pagination and sorting fields)
      Object.keys(filters).forEach(key => {
        if (!['page', 'page_size', 'sort_field', 'sort_order', 'search', 'all_results'].includes(key)) {
          if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
            // Convert filter values to arrays for backend compatibility
            if (Array.isArray(filters[key])) {
              requestPayload[key] = filters[key];
            } else {
              requestPayload[key] = [filters[key]];
            }
          }
        }
      });

      // Add date range if provided
      if (dateRangeState && dateRangeState.currentStart && dateRangeState.currentEnd) {
        requestPayload.date_range = {
          start_date: dateRangeState.currentStart.format('YYYY-MM-DD'),
          end_date: dateRangeState.currentEnd.format('YYYY-MM-DD')
        };
      }



      const response = await api.post('/lms/site-visits/', requestPayload);
      
      // Check if this is still the current request (prevent stale responses)
      if (currentRequestIdRef.current !== requestId) {
        return;
      }
      
      const responseData = response.data;

      const siteVisitsData = responseData.results || responseData.site_visits || [];
      const paginationInfo = responseData.pagination;
      setPaginationInfo(paginationInfo);
      setSiteVisits(siteVisitsData);
    } catch (error) {
      // Check if this is still the current request (prevent stale error handling)
      if (currentRequestIdRef.current !== requestId) {
        return;
      }
      
      console.error('Error fetching site visits:', error);
      setError('Failed to fetch site visits. Please try again.');
      setSiteVisits([]);
      setPaginationInfo({
        total_count: 0,
        current_page: 1,
        page_size: 50,
        total_pages: 1,
        has_next: false,
        has_previous: false
      });
      // Reset the last call reference on error to allow retry
      lastCallRef.current = null;
      
      if (error.isAuthError) {
        setHasApiError(true);
      }
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, hasApiError, isLoading]); // Add dependencies to prevent unnecessary re-renders

  // Debounced version of fetchSiteVisits to prevent rapid successive calls
  const debouncedFetchSiteVisits = useCallback((filters = {}, dateRangeState = null, searchKeyword = null, delay = 300) => {
    // Clear any existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    // Set a new timeout
    debounceTimeoutRef.current = setTimeout(() => {
      fetchSiteVisits(filters, dateRangeState, searchKeyword);
    }, delay);
  }, [fetchSiteVisits]);

  // Fetch site visit details
  const fetchSiteVisitDetail = async (visitId) => {
    if (!isAuthenticated || !visitId) return null;
    
    setIsLoading(true);
    try {
      const response = await api.get(`/lms/site-visits/${visitId}/`).catch(() => ({ data: null }));
      const visitData = response.data;
      return visitData;
    } catch (error) {
      console.error('Error fetching site visit details:', error);
      if (error.isAuthError) {
        setHasApiError(true);
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  };
  
  // Create a new site visit
  const createSiteVisit = async (visitData) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    setIsLoading(true);
    try {
      const response = await api.post('/lms/site-visits/create/', visitData);
      
      // Refresh the site visits data after successful creation
      await fetchSiteVisits(appliedFilters);
      return response.data;
    } catch (error) {
      console.error('Error creating site visit:', error);
      if (error.isAuthError) {
        setHasApiError(true);
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Update site visit
  const updateSiteVisit = async (visitId, payload) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    setIsLoading(true);
    try { 
      const response = await api.put(`/lms/site-visits/${visitId}/`, payload);
      return response.data;
    } catch (error) {
      console.error('Error updating site visit:', error);
      if (error.isAuthError) {
        setHasApiError(true);
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };
  
  // Update site visit status
  const updateSiteVisitStatus = async (visitId, status, reason = null) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    setIsLoading(true);
    try {
      const payload = { status };
      if (reason) payload.reason = reason;
      
      const response = await api.patch(`/lms/site-visits/${visitId}/update-status/`, payload);
      
      // Update local state
      setSiteVisits(prev => 
        prev.map(visit => 
          visit.id === visitId ? { ...visit, status: status } : visit
        )
      );
      return response.data;
    } catch (error) {
      console.error('Error updating site visit status:', error);
      if (error.isAuthError) {
        setHasApiError(true);
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };
  
  // Complete a site visit with feedback
  const completeSiteVisit = async (visitId, notes) => {    
    setIsLoading(true);
    try {
      const payload = { notes: notes };
      const response = await api.post(`/lms/site-visits/${visitId}/complete/`, payload);
      
      // Update local state
      setSiteVisits(prev => 
        prev.map(visit => 
          visit.id === visitId ? { ...visit, status: 'completed', completion_notes: notes } : visit
        )
      );

      return response.data;
    } catch (error) {
      console.error('Error completing site visit:', error);
      if (error.isAuthError) {
        setHasApiError(true);
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };
  
  // Reschedule a site visit
  const rescheduleSiteVisit = async (visitId, payload) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    setIsLoading(true);
    try {
      const response = await api.post(`/lms/site-visits/${visitId}/reschedule/`, payload);

      await fetchSiteVisits(appliedFilters);
      
      return response.data;
    } catch (error) {
      console.error('Error rescheduling site visit:', error);
      if (error.isAuthError) {
        setHasApiError(true);
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };
  
  // Cancel a site visit
  const cancelSiteVisit = async (visitId, notes) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    setIsLoading(true);
    try {
      const payload = { notes: notes };
      
      const response = await api.post(`/lms/site-visits/${visitId}/cancel/`, payload);
      
      // Update local state
      setSiteVisits(prev => 
        prev.map(visit => 
          visit.id === visitId ? { ...visit, status: 'cancelled', cancellation_notes: notes } : visit
        )
      );
      return response.data;
    } catch (error) {
      console.error('Error cancelling site visit:', error);
      if (error.isAuthError) {
        setHasApiError(true);
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Mark site visit as no show (cancels with no-show tag, triggers WhatsApp)
  const markNoShowSiteVisit = async (visitId) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    setIsLoading(true);
    try {
      const response = await api.post(`/lms/site-visits/${visitId}/mark-no-show/`);
      setSiteVisits(prev =>
        prev.map(visit =>
          visit.site_visit_id === visitId || visit.id === visitId
            ? { ...visit, status: 'cancelled' }
            : visit
        )
      );
      return response.data;
    } catch (error) {
      console.error('Error marking site visit as no show:', error);
      if (error.isAuthError) {
        setHasApiError(true);
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };
  
  // Apply filters
  const applyFilters = (filters) => {
    setAppliedFilters(filters);
    fetchSiteVisits(filters);
  };
  
  // Reset the API error state
  const resetApiErrors = () => {
    setHasApiError(false);
    resetAuthErrorState(); // Reset the global auth error state in api.js
  };

  // Force refresh by clearing the last call reference
  const forceRefresh = () => {
    lastCallRef.current = null;

  };

  // Update pagination
  const updatePagination = (page, pageSize) => {
    const newFilters = {
      ...appliedFilters,
      page: page,
      page_size: pageSize
    };
    setAppliedFilters(newFilters);
    fetchSiteVisits(newFilters);
  };
  
  // Cleanup effect to clear timeouts and refs on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      lastCallRef.current = null;
      lastStatusCountsCallRef.current = null;
      currentRequestIdRef.current = null;
    };
  }, []);
  
 
  // Site visit status counts
  const fetchSiteVisitStatusCounts = async (dateRangeState = null) => {    
    if (!isAuthenticated || hasApiError) return {};
    
    // Check if this is a duplicate API call
    const currentDateRange = dateRangeState ? {
      start_date: dateRangeState.currentStart?.format('YYYY-MM-DD'),
      end_date: dateRangeState.currentEnd?.format('YYYY-MM-DD'),
      all_results: dateRangeState.all_results || dateRangeState.filter?.id === 'All'
    } : null;
    
    if (JSON.stringify(currentDateRange) === JSON.stringify(lastStatusCountsCallRef.current)) {
      return statusCounts; // Return current status counts without making API call
    }
    
    // Update the last call reference
    lastStatusCountsCallRef.current = currentDateRange;
    
    try {
      // Determine the date range to use
      let startDate;
      let endDate;

      if (dateRangeState && dateRangeState.currentStart && dateRangeState.currentEnd) {
        // Use provided date range if valid
        startDate = dateRangeState.currentStart;
        endDate = dateRangeState.currentEnd;
      } else {
        // Default to current date
        startDate = dayjs();
        endDate = dayjs(); // Today
      }

      // Create the request payload for POST request
      const requestPayload = {
        // Date range
        date_range: {
          start_date: startDate.format('YYYY-MM-DD'),
          end_date: endDate.format('YYYY-MM-DD')
        },
        all_results: dateRangeState?.all_results || dateRangeState?.filter?.id === 'All'
      };

      // Make the POST API request
      const response = await api.post('/lms/site-visits/status-counts/', requestPayload);
      
      if (response && response.data) {
        const statusCountsData = response.data;
        setStatusCounts(statusCountsData); // Update the state
        return statusCountsData;
      } else {
        return {};
      }
    } catch (error) {      
      if (error.isAuthError) {
        setHasApiError(true);
      }
      setStatusCounts({});
      return {};
    }
  };

  
  // Context value
  const value = {
    // State
    siteVisits,
    allSiteVisits,
    filters,
    isLoading,
    error,
    hasApiError,
    appliedFilters,
    pagination: paginationInfo,
    statusCounts,
    setStatusCounts,
    updatePagination,
    
    // Actions
    fetchSiteVisits,
    fetchSiteVisitDetail,
    createSiteVisit,
    updateSiteVisit,
    updateSiteVisitStatus,
    completeSiteVisit,
    rescheduleSiteVisit,
    cancelSiteVisit,
    markNoShowSiteVisit,
    applyFilters,
    resetApiErrors,
    forceRefresh,
    // Setters
    setAppliedFilters,
    setFilters: setAppliedFilters,
    setAllSiteVisits,
    fetchSiteVisitStatusCounts
  };
  
  return (
    <SiteVisitContext.Provider value={value}>
      {children}
    </SiteVisitContext.Provider>
  );
};
